import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Radio,
  Wifi,
  Antenna,
  Smartphone,
  Satellite,
  Cpu,
  Users,
  BookOpen,
  Award,
  TrendingUp,
  Settings,
  Globe,
  Shield,
  Star
} from 'lucide-react';
import '../Css_files/ece.css';
import Navbar from './Navbar';
import Footer from './Footer';

const Ece = () => {
  const programs = [
    {
      title: 'Electronics & Communication (Beginner to Advanced)',
      duration: '6 Months',
      description: 'Learn the fundamentals of electronics and communication systems, designed for beginners stepping into the world of technology.',
      features: ['Basic Electronics', 'Hands-on Projects', 'Live Classes', 'Certificate of Completion']
    },
    {
      title: 'Signal Processing & Communication Mastery',
      duration: '4 Months',
      description: 'Gain in-depth knowledge of digital signal processing and modern communication technologies with industry tools.',
      features: ['DSP Concepts', 'Practical Demos', 'AI Integration', 'Mini Research Project']
    },
    {
      title: 'VLSI & Embedded Systems Professional',
      duration: '5 Months',
      description: 'Specialized program focusing on VLSI design, chip development, and embedded system applications.',
      features: ['VLSI Tools', 'IoT Devices', 'CAD Software Training', 'Portfolio Project']
    }
  ];

  const faculty = [
    {
      name: 'Dr. Anita Sharma',
      position: 'Course Mentor',
      specialization: 'Wireless Communications & 5G',
      experience: '18+ years',
      image: 'https://images.pexels.com/photos/3182821/pexels-photo-3182821.jpeg?auto=compress&cs=tinysrgb&w=300',
      alt: 'ECE Online Course Mentor Dr. Anita Sharma'
    },
    {
      name: 'Dr. Vikram Singh',
      position: 'Senior Trainer',
      specialization: 'Signal Processing & AI',
      experience: '15+ years',
      image: 'https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=300',
      alt: 'ECE Online Course Trainer Dr. Vikram Singh'
    },
    {
      name: 'Dr. Priya Gupta',
      position: 'Instructor',
      specialization: 'VLSI & Embedded Systems',
      experience: '12+ years',
      image: 'https://images.pexels.com/photos/3182824/pexels-photo-3182824.jpeg?auto=compress&cs=tinysrgb&w=300',
      alt: 'ECE Online Course Instructor Dr. Priya Gupta'
    }
  ];

  const testimonials = [
    {
      name: 'Rahul Verma',
      role: 'Now at Qualcomm',
      year: '2021 Batch',
      content: 'This ECE online program gave me strong fundamentals in communication systems. The projects and mentorship prepared me for the semiconductor industry.',
      image: 'https://images.pexels.com/photos/3182773/pexels-photo-3182773.jpeg?auto=compress&cs=tinysrgb&w=150',
      alt: 'ECE Online Learning Student Rahul Verma'
    },
    {
      name: 'Kavya Nair',
      role: 'Research Scientist at Intel',
      year: '2020 Batch',
      content: 'Hands-on VLSI design and embedded systems training helped me build real-world projects and kickstart my career in chip design.',
      image: 'https://images.pexels.com/photos/3182824/pexels-photo-3182824.jpeg?auto=compress&cs=tinysrgb&w=150',
      alt: 'ECE Online Learning Student Kavya Nair'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Online Electronics & Communication Courses | Profenaa Learning</title>
        <meta
          name="description"
          content="Join Profenaa’s Electronics & Communication online courses. Learn 5G, VLSI, Embedded Systems, and Signal Processing with industry experts. Get certified and job-ready."
        />
        <meta
          name="keywords"
          content="Electronics online course, Communication engineering course, VLSI training, Embedded systems course, Online learning Profenaa"
        />
      </Helmet>

      <Navbar />
      <div className="ece-page">

        {/* Hero Section */}
        <section className="ece-hero">
          <div className="ece-hero-content">
            <h1 className="ece-hero-title">
              Electronics & Communication Online Courses
              <span className="ece-hero-subtitle">Learn, Build & Succeed with Profenaa</span>
            </h1>
            <p className="ece-hero-description">
              Master the skills powering the connected world. From 5G networks to embedded systems — learn step by step with live classes, hands-on labs, and career guidance.
            </p>
            <div className="ece-hero-actions">
              <button className="ece-btn ece-btn-primary">Enroll Now</button>
              <button className="ece-btn ece-btn-secondary">Download Syllabus</button>
            </div>
          </div>
        </section>

        {/* Programs Section */}
        <section className="ece-programs">
          <div className="ece-container">
            <h2 className="ece-section-title">Our Popular Programs</h2>
            <div className="ece-programs-grid">
              {programs.map((program, index) => (
                <div key={index} className="ece-program-card">
                  <h3>{program.title}</h3>
                  <span>{program.duration}</span>
                  <p>{program.description}</p>
                  <ul>
                    {program.features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                  <button className="ece-program-btn">Learn More</button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Faculty Section */}
        <section className="ece-faculty">
          <div className="ece-container">
            <h2 className="ece-section-title">Meet Your Instructors</h2>
            <div className="ece-faculty-grid">
              {faculty.map((member, index) => (
                <div key={index} className="ece-faculty-card">
                  <img src={member.image} alt={member.alt} />
                  <h3>{member.name}</h3>
                  <p>{member.position}</p>
                  <p>{member.specialization}</p>
                  <span>{member.experience}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="ece-testimonials">
          <div className="ece-container">
            <h2 className="ece-section-title">What Our Learners Say</h2>
            <div className="ece-testimonials-grid">
              {testimonials.map((t, index) => (
                <div key={index} className="ece-testimonial-card">
                  <p>"{t.content}"</p>
                  <div className="ece-testimonial-author">
                    <img src={t.image} alt={t.alt} />
                    <div>
                      <h4>{t.name}</h4>
                      <p>{t.role}</p>
                      <p>{t.year}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="ece-cta">
          <div className="ece-container">
            <h2>Start Your Electronics & Communication Journey Today</h2>
            <p>Enroll in our online programs and gain hands-on skills with expert mentorship and certification.</p>
            <div className="ece-cta-actions">
              <button className="ece-btn ece-btn-primary">Enroll Now</button>
              <button className="ece-btn ece-btn-outline">Talk to an Advisor</button>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default Ece;
